// import packages
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  BlockStack,
  Box,
  Button,
  Icon,
  LegacyCard as Card,
  FormLayout,
  Layout,
  PageActions,
  Text,
  TextField,
} from "@shopify/polaris";
import { EmailFollowUpIcon } from "@shopify/polaris-icons";
// import hoc
import { withErrorBoundary } from "lib/hoc";

// helpers
import { baseHelper, errorHelper } from "lib/helpers";

// constant
import constant from "lib/constant/constant";

// helper components
import { Banner } from "lib/components";
import { Footer } from "layout/public/components";

// contexts
import { PublicContext } from "lib/context";

// import gql
import { GET_BRANDS } from "app/public/apollo/queries";
import { RESET_PASSWORD } from "app/public/apollo/mutations";

// yup validation
import { useNavigate } from "react-router-dom";
import validate from "./yup";

// sub-features
import { AccountType, Brand } from "./subFeatureItems";

const ResetPassword = () => {
  const { cms } = useContext(PublicContext);
  const navigate = useNavigate();

  const { EMAIL, ACCOUNT_TYPE, BRAND_NAME, SHOW_BRAND_NAME, SHOW_ACCOUNT_TYPE, gql } = constant;
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(60);
  const [isVendor, setIsVendor] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  // states
  const [values, setValues] = useState({
    email: "",
    isEmailDisable: false,
    isAccountTypeDisable: false,
    accountType: "",
    brandName: "",
  });
  const [showFields, setShowFields] = useState({
    showAccountType: false,
    showBrandName: false,
  });
  const [errorMessage, setErrorMessage] = useState(false);
  const [userBrands, setUserBrands] = useState({
    seller: [],
    vendor: [],
  });
  const [banner, setBanner] = useState({
    isOpen: false,
    status: "",
    title: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitButton, setSubmitState] = useState(false);
  const [fetchNewAccount, setFetchNewAccount] = useState(true);

  // gql states
  const [getAccount, { data: accountData, loading: getBrandLoading }] = useLazyQuery(GET_BRANDS);
  const [resetPassword, { loading, data }] = useMutation(RESET_PASSWORD);

  const updateSubmitButton = useCallback(
    (field) => {
      const isAnyValidationError = errorMessage && !!errorMessage[field];
      const isAllValuesFilled = values[field];
      setSubmitState(!!isAllValuesFilled && !isAnyValidationError);
    },
    [errorMessage, values]
  );

  useEffect(() => {
    if (errorMessage) {
      if (!(showFields.showAccountType || showFields.showBrandName)) {
        updateSubmitButton(EMAIL);
      }
      if (showFields.showAccountType) {
        updateSubmitButton(ACCOUNT_TYPE);
      }
      if (showFields.showBrandName) {
        updateSubmitButton(BRAND_NAME);
      }
    }
  }, [showFields, errorMessage, updateSubmitButton, EMAIL, ACCOUNT_TYPE, BRAND_NAME]);

  const setFieldStatus = useCallback((field, value) => {
    setShowFields((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  }, []);

  useEffect(() => {
    setFieldStatus(SHOW_BRAND_NAME, false);
    if (
      (values.accountType === constant.SELLER || values.accountType === constant.VENDOR) &&
      showFields.showAccountType
    ) {
      setFieldStatus(SHOW_BRAND_NAME, true);
    }
  }, [
    SHOW_BRAND_NAME,
    showFields.showBrandName,
    values.accountType,
    userBrands,
    setFieldStatus,
    showFields.showAccountType,
  ]);

  const handleChange = useCallback(async (key, value) => {
    setValues((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }, []);
  const handleValidation = async (field, value) => {
    const validationError = await validate(field, value, cms);
    setErrorMessage((prevState) => ({
      ...prevState,
      [field]: validationError,
    }));
  };

  const sendEmail = useCallback(
    async (accountType = "", brandName = "") => {
      const inputData = {};
      inputData.email = values.email && values.email.toLowerCase();
      inputData.userType = accountType || values.accountType;
      if (values && values.accountType === constant.SELLER) {
        inputData.brandName = brandName || values.brandName;
      }
      if (values && values.accountType === constant.VENDOR) {
        inputData.introductorySeller = brandName || values.brandName;
      }
      try {
        const res = await resetPassword({
          variables: { input: inputData },
        });
        const resData = baseHelper.getResponseData(res.data, gql.FORGET_PASSWORD);
        if (!resData) {
          const resError = baseHelper.getResponseError(res.data, gql.FORGET_PASSWORD);
          setBanner({
            isOpen: true,
            isScrollTop: true,
            status: constant.CRITICAL,
            title: resError,
          });
        } else {
          setBanner({ isOpen: false, status: "", title: "" });
          setIsEmailSent(true);
          setResendSeconds(60);
        }
      } catch (exception) {
        setBanner({
          isOpen: true,
          isScrollTop: true,
          status: constant.CRITICAL,
          title: errorHelper.parse(exception),
        });
      }
    },
    [values, resetPassword, gql.FORGET_PASSWORD]
  );

  const listBrand = useCallback((response) => {
    setUserBrands((prevState) => ({
      ...prevState,
      seller: response.getBrands.data.shops,
      vendor: response.getBrands.data.associatedSellers,
    }));
  }, []);

  const showSellerStore = useCallback(
    async (response) => {
      listBrand(response);
      setFieldStatus(SHOW_BRAND_NAME, true);
    },
    [SHOW_BRAND_NAME, listBrand, setFieldStatus]
  );
  const showAccountAndBrand = useCallback(
    async (response) => {
      listBrand(response);
      setFieldStatus(SHOW_ACCOUNT_TYPE, true);
    },
    [SHOW_ACCOUNT_TYPE, listBrand, setFieldStatus]
  );
  const userStoreList = useCallback(
    async (response) => {
      const resData = baseHelper.getResponseData(response, gql.GET_BRANDS);
      const resError = baseHelper.getResponseError(response, gql.GET_BRANDS);
      const isSellerRole = resData && !!resData.shops.length;
      const isVendorRole = resData && !!resData.associatedSellers.length;
      if (isVendorRole && resData.associatedSellers) {
        setIsVendor(!!resData.associatedSellers.filter((item) => item).length);
      }
      if (isSellerRole && resData.shops) {
        setIsSeller(!!resData.shops.filter((item) => item).length);
      }
      const isBothRole = resData && !!resData.shops.length && !!resData.associatedSellers.length;
      if (resData) {
        setBanner({ isOpen: false });
      }
      if (isSellerRole && isVendorRole) {
        showAccountAndBrand(response);
      }
      if (isSellerRole && !isVendorRole) {
        showSellerStore(response);
        const sellerBrandName = resData && resData.shops && resData.shops.length && resData.shops[0].value;
        setValues((prevState) => ({
          ...prevState,
          accountType: constant.SELLER,
          brandName: sellerBrandName,
        }));
        if (resData.shops.length) {
          setFieldStatus(SHOW_ACCOUNT_TYPE, true);
        }
      }
      if (!isSellerRole && isVendorRole) {
        showSellerStore(response);
        const vendorBrandName =
          resData &&
          resData.associatedSellers &&
          resData.associatedSellers.length &&
          resData.associatedSellers[0].value;
        setValues((prevState) => ({
          ...prevState,
          accountType: constant.VENDOR,
          brandName: vendorBrandName,
        }));
        if (resData.associatedSellers.length) {
          setFieldStatus(SHOW_ACCOUNT_TYPE, true);
        }
      }
      if ((!isSellerRole && !isVendorRole && !isBothRole) || resError) {
        setValues((prevState) => ({
          ...prevState,
          email: "",
        }));
        setBanner((prevState) => ({
          ...prevState,
          isOpen: true,
          status: constant.CRITICAL,
          title: resError || cms("common.message.error.unauthorized"),
        }));
        return;
      }
      setValues((prevState) => ({
        ...prevState,
        isEmailDisable: true,
      }));
    },
    [gql.GET_BRANDS, cms, showAccountAndBrand, showSellerStore, SHOW_ACCOUNT_TYPE, setFieldStatus]
  );
  useEffect(() => {
    if (!loading && !getBrandLoading && isLoading) {
      if (data) {
        const resData = baseHelper.getResponseData(data, gql.FORGET_PASSWORD);
        if (!resData) {
          const resError = baseHelper.getResponseError(data, gql.FORGET_PASSWORD);
          setBanner((prevState) => ({
            ...prevState,
            isOpen: true,
            status: constant.CRITICAL,
            title: resError,
          }));
        }
      }
      setIsLoading(false);
    }
  }, [data, accountData, loading, getBrandLoading, isLoading, gql.FORGET_PASSWORD, cms, userStoreList]);

  useEffect(() => {
    if (accountData && !isEmailSent && fetchNewAccount) {
      userStoreList(accountData);
      setFetchNewAccount(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData]);

  useEffect(() => {
    if (!isEmailSent || resendSeconds <= 0) {
      return undefined;
    }
    const countdown = setTimeout(() => {
      setResendSeconds((prevState) => prevState - 1);
    }, 1000);
    return () => clearTimeout(countdown);
  }, [isEmailSent, resendSeconds]);

  const fetchAccountTypes = async () => {
    setIsLoading(true);
    const isAnyValidationError = errorMessage && !!errorMessage.email;
    const isAllValuesFilled = values.email;
    if (!isAnyValidationError && isAllValuesFilled) {
      try {
        getAccount({ variables: { input: { email: values.email } } });
      } catch (exception) {
        setBanner((prevState) => ({
          ...prevState,
          isOpen: true,
          status: constant.CRITICAL,
          title: errorHelper.parse(exception),
        }));
      }
    }
  };

  const fetchAccounts = () => {
    setIsLoading(true);
    setValues((prevState) => ({
      ...prevState,
      isAccountTypeDisable: true,
    }));
    setFieldStatus(SHOW_BRAND_NAME, true);
  };

  const displayBrands = (role) => {
    if (role === constant.VENDOR) {
      return userBrands.vendor.length ? userBrands.vendor : [];
    }
    if (role === constant.SELLER) {
      return userBrands.seller.length ? userBrands.seller : [];
    }
    return false;
  };

  const renderBrandName = () => {
    if (!showFields.showBrandName) {
      return null;
    }
    return (
      <Brand
        handleChange={handleChange}
        handleValidation={handleValidation}
        errorMessage={errorMessage}
        brandName={values.brandName}
        brands={displayBrands(values.accountType)}
        cms={cms}
      />
    );
  };

  const renderAccountType = () => {
    if (!showFields.showAccountType) {
      return null;
    }
    return (
      <AccountType
        handleChange={handleChange}
        handleValidation={handleValidation}
        errorMessage={errorMessage}
        accountType={values.accountType}
        isVendor={isVendor}
        isSeller={isSeller}
        isAccountTypeDisable={values.isAccountTypeDisable}
        cms={cms}
      />
    );
  };

  const onSubmit = async () => {
    const isEmailSubmit = !(showFields.showAccountType || showFields.showBrandName);
    const isAccountTypeSubmit = showFields.showAccountType;
    const isBrandNameSubmit = showFields.showBrandName;
    if (isEmailSubmit) {
      await handleValidation(EMAIL, values.email);
      fetchAccountTypes();
      setFetchNewAccount(true);
    }
    if (isAccountTypeSubmit) {
      await handleValidation(ACCOUNT_TYPE, values.accountType);
      fetchAccounts();
    }
    if (isBrandNameSubmit) {
      await handleValidation(BRAND_NAME, values.brandName);
      sendEmail();
    }
  };

  const dismissBanner = () => setBanner({ isOpen: false, status: "", title: "" });

  const handleResend = () => {
    if (resendSeconds > 0 || loading) {
      return;
    }
    sendEmail();
  };

  const inboxTitle = cms("message.checkInbox.title") || "Check Your Inbox";
  const inboxDescriptionIntro =
    cms("message.checkInbox.descriptionIntro") || "We have sent a secure password reset link to";
  const inboxDescriptionOutro =
    cms("message.checkInbox.descriptionOutro") ||
    "Please follow the instructions within the email to set a new password and secure your account.";
  const resendPrompt = cms("message.checkInbox.resendPrompt") || "Didn't receive the email? Resend link";
  const receivedPrompt = cms("message.checkInbox.receivedPrompt") || "Email received. Please ";
  const loginLink = cms("message.checkInbox.loginLink") || "Login here";

  return (
    <>
      <Banner isOpen={banner.isOpen} status={banner.status} title={banner.title} onDismiss={() => dismissBanner()} />
      <br />
      <div className="resetPasswordRoot">
        <Layout>
          <Layout.AnnotatedSection title={cms("title")} description={cms("description")}>
            <Card sectioned>
              <FormLayout>
                <TextField
                  id="email"
                  label={`${cms("common.label.email")}*`}
                  value={values.email || ""}
                  placeholder={cms("common.placeholder.emailAddress")}
                  onChange={(value) => handleChange(EMAIL, value)}
                  onBlur={() => handleValidation(EMAIL, values.email)}
                  disabled={values.isEmailDisable}
                  error={errorMessage && errorMessage.email}
                />
                {renderAccountType()}
                {renderBrandName()}
              </FormLayout>
            </Card>
            <PageActions
              primaryAction={{
                id: "resetPassword",
                content: cms("button.primary"),
                onAction: () => onSubmit(),
                disabled: !submitButton,
                loading: loading || getBrandLoading,
              }}
              secondaryActions={[
                {
                  id: "cancel",
                  content: cms("common.button.cancel"),
                  onAction: () => navigate("/login"),
                },
              ]}
            />
          </Layout.AnnotatedSection>
        </Layout>
        {isEmailSent && (
          <div className="resetPasswordOverlay">
            <div className="resetPasswordModal">
              <Box paddingBlockEnd="400">
                <Icon source={EmailFollowUpIcon} tone="base" />
              </Box>
              <Text variant="headingLg" as="h2" alignment="center" className="resetPasswordModalTitle">
                {inboxTitle}
              </Text>
              <Text variant="bodyMd" as="p" alignment="center" tone="subdued" className="resetPasswordModalDescription">
                {inboxDescriptionIntro}{" "}
                <Text as="span" fontWeight="semibold">
                  {values.email}
                </Text>
                . {inboxDescriptionOutro}
              </Text>
              <Button id="resendResetEmail" fullWidth onClick={handleResend} disabled={resendSeconds > 0 || loading}>
                {resendPrompt} {resendSeconds > 0 ? `(available in ${resendSeconds}s)` : ""}
              </Button>
              <BlockStack className="resetPasswordModalFooterText">
                {receivedPrompt}
                <Button variant="plain" onClick={() => navigate("/login")}>
                  {loginLink}
                </Button>
              </BlockStack>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default withErrorBoundary(ResetPassword);
